import { 
  Mail, 
  Phone, 
  MessageCircle, 
  Star, 
  Award, 
  MapPin,
  Home,
  ArrowLeft,
  Calendar,
  Trophy,
  Languages,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Agent } from '../types/agent';
import { Property } from '../types/property';
import { PropertyCard } from './PropertyCard';
import { apiFetch, API_BASE } from '../lib/api-fetch';
import { logger } from '../services/logger.service';

interface AgentProfileProps {
  agent: Agent;
  onBack: () => void;
  onPropertySelect: (property: Property) => void;
}

export function AgentProfile({ agent, onBack, onPropertySelect }: AgentProfileProps) {
  const [agentProperties, setAgentProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  // Fetch listings for this agent via GET /listings?agentId= (F-046 fix)
  useEffect(() => {
    async function fetchAgentProperties() {
      setLoadingProperties(true);
      try {
        // Prefer P24 ID (externalAgentId) — listings in DynamoDB are indexed by P24 ID for imported agents
        const agentId = agent.externalAgentId || (agent as any).agentId || agent.id;
        const response = await apiFetch(`${API_BASE}/listings?agentId=${encodeURIComponent(agentId)}`);
        const data = await response.json();
        const listings = data.data?.listings ?? data.listings ?? [];

        // Normalize to Property shape for PropertyCard
        const normalized: Property[] = listings.map((l: any) => ({
          id: l.somethingPropertyId || l.id || String(l.listingId),
          slug: l.somethingPropertyId || l.id || String(l.listingId),
          title: l.title || 'Untitled Property',
          type: (l.propertyType || 'House') as Property['type'],
          status: 'Published' as Property['status'],
          listingStatus: l.status as Property['listingStatus'],
          somethingPropertyId: l.somethingPropertyId,
          address: {
            street: typeof l.location === 'string' ? l.location : l.location?.address || '',
            suburb: '', city: '', province: '', postalCode: '',
          },
          geo: { lat: 0, lng: 0 },
          price: l.price || 0,
          facts: { bedrooms: l.bedrooms, bathrooms: l.bathrooms, floorSize: l.squareMeters },
          amenities: [],
          media: (l.images || []).map((img: any, i: number) => ({
            id: `img-${i}`, url: typeof img === 'string' ? img : img?.url || '',
            alt: l.title || '', isCover: i === 0, order: i,
          })),
          description: l.description || '',
          listingDate: new Date(l.createdAt || Date.now()).toISOString(),
          agentId: l.agentId || agentId,
          agent: l.agentName ? {
            id: l.agentId || agentId,
            name: l.agentName, email: l.agentEmail || '', phone: l.agentPhone || '',
            whatsapp: l.agentPhone || '', avatarUrl: l.agentPhotoUrl || undefined,
          } : undefined,
          visibility: 'Public' as Property['visibility'],
          flags: {},
          mandateType: l.mandateType,
        }));

        setAgentProperties(normalized);
      } catch (error) {
        logger.error('Error fetching agent listings', error as Error, undefined, 'AgentProfile');
        setAgentProperties([]);
      } finally {
        setLoadingProperties(false);
      }
    }
    
    fetchAgentProperties();
  }, [agent.id]);

  const handleWhatsAppClick = () => {
    const whatsappNumber = agent.whatsapp.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(`Hi ${agent.name}, I found your profile on Something Property and would like to discuss my property needs.`);
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="min-h-screen bg-muted/30 pt-16">
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={onBack}
          data-testid="agent-profile-back"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Agents
        </Button>
        {/* Agent Header */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                  {agent.avatar && !avatarError ? (
                    <img
                      src={agent.avatar}
                      alt={agent.name}
                      className="w-full h-full object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <span className="text-4xl font-semibold text-muted-foreground">
                      {getInitials(agent.name)}
                    </span>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="mb-4">
                  <h1 className="mb-2">{agent.name}</h1>
                  <p className="text-lg text-muted-foreground">{agent.title}</p>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <div>
                      <div className="font-semibold">{agent.rating}</div>
                      <div className="text-xs text-muted-foreground">Rating</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <div>
                      <div className="font-semibold">{agent.soldProperties}</div>
                      <div className="text-xs text-muted-foreground">Properties Sold</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="font-semibold">{agent.totalListings}</div>
                      <div className="text-xs text-muted-foreground">Active Listings</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="font-semibold">{agent.yearsExperience} Years</div>
                      <div className="text-xs text-muted-foreground">Experience</div>
                    </div>
                  </div>
                </div>

                {/* Contact Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleWhatsAppClick} size="lg" data-testid="agent-profile-whatsapp">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Me
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.location.href = `mailto:${agent.email}`}
                    data-testid="agent-profile-email"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => window.location.href = `tel:${agent.phone}`}
                    data-testid="agent-profile-call"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="about" className="space-y-6">
          <TabsList>
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="listings">
              Listings ({agentProperties.length})
            </TabsTrigger>
            <TabsTrigger value="contact">Contact Info</TabsTrigger>
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About Me</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {agent.bio}
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Specialities */}
              {agent.specializations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Specialities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {agent.specializations.map((spec, idx) => (
                        <Badge key={idx} variant="secondary">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Areas Served */}
              {agent.areasServed && agent.areasServed.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Areas Served
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {agent.areasServed.map((area, idx) => (
                        <Badge key={idx} variant="outline">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Property Types */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Property Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {agent.propertyTypes.map((type, idx) => (
                      <Badge key={idx} variant="outline">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Languages */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Languages className="w-5 h-5" />
                    Languages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {agent.languages.map((lang, idx) => (
                      <Badge key={idx} variant="secondary">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Achievements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Achievements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {agent.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Award className="w-4 h-4 mt-0.5 text-yellow-600 flex-shrink-0" />
                        <span className="text-sm">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Member Since */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {formatDate(agent.joinedDate)}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {loadingProperties ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-12 h-12 mx-auto text-muted-foreground mb-4 animate-spin" />
                  <h3 className="mb-2">Loading Listings</h3>
                  <p className="text-muted-foreground">
                    Fetching properties listed by {agent.name.split(' ')[0]}...
                  </p>
                </CardContent>
              </Card>
            ) : agentProperties.length > 0 ? (
              <>
                <div className="mb-4">
                  <h3>{agent.name}'s Listings</h3>
                  <p className="text-muted-foreground">
                    Browse all properties currently listed by {agent.name.split(' ')[0]}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {agentProperties.map((property) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      onClick={() => onPropertySelect(property)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Home className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="mb-2">No Active Listings</h3>
                  <p className="text-muted-foreground">
                    {agent.name} doesn't have any active listings at the moment.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>
                  Get in touch with {agent.name} for all your property needs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium mb-1">Email</div>
                    <a 
                      href={`mailto:${agent.email}`}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {agent.email}
                    </a>
                  </div>
                </div>

                <Separator />

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-muted">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium mb-1">Phone</div>
                    <a 
                      href={`tel:${agent.phone}`}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {agent.phone}
                    </a>
                  </div>
                </div>

                <Separator />

                {/* WhatsApp */}
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                    <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium mb-1">WhatsApp</div>
                    <div className="text-muted-foreground mb-2">{agent.whatsapp}</div>
                    <Button
                      onClick={handleWhatsAppClick}
                      variant="outline"
                      className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950"
                      data-testid="agent-profile-whatsapp-chat"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Start WhatsApp Chat
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}